<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

/**
 * Body of an action_params entry for `send_whatsapp_template`.
 * Shape produced/consumed:
 *   [{
 *      inbox_id: Number,
 *      template_params: {
 *        name, language, namespace, category,
 *        processed_params: { body: { '1': 'literal or {{contact.name}}', ... } }
 *      }
 *   }]
 */
const props = defineProps({
  modelValue: {
    type: [Array, Object, String],
    default: () => [],
  },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const allInboxes = useMapGetter('inboxes/getInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const value = computed(() => {
  if (Array.isArray(props.modelValue) && props.modelValue[0]) {
    return props.modelValue[0];
  }
  return {};
});

const liquidHints = [
  '{{contact.name}}',
  '{{contact.email}}',
  '{{contact.phone_number}}',
  '{{conversation.id}}',
  '{{inbox.name}}',
];

function updateValue(next) {
  emit('update:modelValue', [next]);
}

function buildEmptyBody(tpl) {
  const bodyComponent = (tpl.components || []).find(
    c => (c.type || '').toUpperCase() === 'BODY'
  );
  const text = bodyComponent?.text || '';
  const matches = text.match(/{{\s*([^{}\s]+)\s*}}/g) || [];
  const result = {};
  matches.forEach(m => {
    const key = m.replace(/[{}]/g, '').trim();
    result[key] = '';
  });
  return result;
}

const inboxId = computed({
  get: () => value.value.inbox_id || null,
  set: id => updateValue({ inbox_id: id, template_params: null }),
});

const fallbackInboxId = computed({
  get: () => value.value.fallback_inbox_id || null,
  set: id => updateValue({ ...value.value, fallback_inbox_id: id || null }),
});

const fallbackOptions = computed(() => {
  const messagingTypes = [
    'Channel::Whatsapp',
    'Channel::Email',
    'Channel::Sms',
    'Channel::TwilioSms',
  ];
  return (allInboxes.value || []).filter(i => {
    if (i.id === inboxId.value) return false;
    if (!messagingTypes.includes(i.channel_type)) return false;
    if (i.channel_type === 'Channel::TwilioSms' && i.medium !== 'sms')
      return false;
    return true;
  });
});

function fallbackOptionLabel(inbox) {
  const map = {
    'Channel::Whatsapp': 'WhatsApp',
    'Channel::Email': 'Email',
    'Channel::Sms': 'SMS',
    'Channel::TwilioSms': 'SMS',
  };
  const channel = map[inbox.channel_type] || inbox.channel_type;
  return `${inbox.name} — ${channel}`;
}

const templates = computed(() => {
  if (!inboxId.value) return [];
  return getFilteredWhatsAppTemplates.value(inboxId.value) || [];
});

const selectedTemplate = computed(() => {
  const tp = value.value.template_params;
  if (!tp?.name) return null;
  return templates.value.find(
    tpl => tpl.name === tp.name && (tpl.language || '') === (tp.language || '')
  );
});

function buildEmptyButtons(tpl) {
  const buttonsComponent = (tpl.components || []).find(
    c => (c.type || '').toUpperCase() === 'BUTTONS'
  );
  if (!buttonsComponent) return undefined;
  const buttons = (buttonsComponent.buttons || []).map(btn => {
    if (btn.type === 'URL' && btn.url && btn.url.includes('{{1}}')) {
      return { type: 'url', parameter: '' };
    }
    return null;
  });
  return buttons.some(b => b !== null) ? buttons : undefined;
}

const selectedTemplateKey = computed({
  get: () => {
    const tp = value.value.template_params;
    if (!tp?.name) return null;
    return `${tp.name}::${tp.language || ''}`;
  },
  set: key => {
    if (!key) {
      updateValue({ ...value.value, template_params: null });
      return;
    }
    const [name, language] = key.split('::');
    const tpl = templates.value.find(
      item => item.name === name && (item.language || '') === language
    );
    if (!tpl) return;
    const emptyButtons = buildEmptyButtons(tpl);
    const processed = { body: buildEmptyBody(tpl) };
    if (emptyButtons) processed.buttons = emptyButtons;
    updateValue({
      ...value.value,
      template_params: {
        name: tpl.name,
        language: tpl.language || 'en_US',
        namespace: tpl.namespace || '',
        category: tpl.category || 'UTILITY',
        processed_params: processed,
      },
    });
  },
});

const bodyVariableKeys = computed(() => {
  const tpl = selectedTemplate.value;
  if (!tpl) return [];
  const bodyComponent = (tpl.components || []).find(
    c => (c.type || '').toUpperCase() === 'BODY'
  );
  const text = bodyComponent?.text || '';
  const matches = text.match(/{{\s*([^{}\s]+)\s*}}/g) || [];
  const keys = [];
  matches.forEach(m => {
    const key = m.replace(/[{}]/g, '').trim();
    if (!keys.includes(key)) keys.push(key);
  });
  return keys;
});

const bodyParams = computed(
  () => value.value.template_params?.processed_params?.body || {}
);

// URL buttons with dynamic {{1}} placeholder — need a separate parameter input
const urlButtonComponents = computed(() => {
  const tpl = selectedTemplate.value;
  if (!tpl) return [];
  const buttonsComponent = (tpl.components || []).find(
    c => (c.type || '').toUpperCase() === 'BUTTONS'
  );
  if (!buttonsComponent) return [];
  return (buttonsComponent.buttons || [])
    .map((btn, idx) => ({ ...btn, originalIndex: idx }))
    .filter(btn => btn.type === 'URL' && btn.url && btn.url.includes('{{1}}'));
});

const buttonParams = computed(
  () => value.value.template_params?.processed_params?.buttons || []
);

function updateBodyParam(key, val) {
  const tp = value.value.template_params || {};
  updateValue({
    ...value.value,
    template_params: {
      ...tp,
      processed_params: {
        ...(tp.processed_params || {}),
        body: { ...bodyParams.value, [key]: val },
      },
    },
  });
}

function updateButtonParam(originalIndex, val) {
  const tp = value.value.template_params || {};
  const buttons = [...buttonParams.value];
  while (buttons.length <= originalIndex) buttons.push(null);
  buttons[originalIndex] = { type: 'url', parameter: val };
  updateValue({
    ...value.value,
    template_params: {
      ...tp,
      processed_params: {
        ...(tp.processed_params || {}),
        buttons,
      },
    },
  });
}

function variableLabel(key) {
  return `{{${key}}}`;
}

function templateOptionLabel(tpl) {
  return `${tpl.name} [${tpl.language || 'en'}]`;
}

watch(
  whatsappInboxes,
  list => {
    if (
      !list?.length &&
      (value.value.inbox_id || value.value.template_params)
    ) {
      emit('update:modelValue', [{}]);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="w-full mt-2 flex flex-col gap-2">
    <div>
      <label class="text-xs text-n-slate-11 mb-1 block">
        {{ t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.INBOX_LABEL') }}
      </label>
      <select v-model="inboxId" class="m-0 w-full">
        <option :value="null" disabled>
          {{ t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.INBOX_PLACEHOLDER') }}
        </option>
        <option
          v-for="inbox in whatsappInboxes"
          :key="inbox.id"
          :value="inbox.id"
        >
          {{ inbox.name }}
        </option>
      </select>
    </div>

    <div v-if="inboxId">
      <label class="text-xs text-n-slate-11 mb-1 block">
        {{ t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.TEMPLATE_LABEL') }}
      </label>
      <select v-model="selectedTemplateKey" class="m-0 w-full">
        <option :value="null" disabled>
          {{ t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.TEMPLATE_PLACEHOLDER') }}
        </option>
        <option
          v-for="tpl in templates"
          :key="`${tpl.name}::${tpl.language || ''}`"
          :value="`${tpl.name}::${tpl.language || ''}`"
        >
          {{ templateOptionLabel(tpl) }}
        </option>
      </select>
      <p v-if="!templates.length" class="text-xs text-n-slate-10 mt-1">
        {{ t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.NO_TEMPLATES') }}
      </p>
    </div>

    <div v-if="bodyVariableKeys.length" class="flex flex-col gap-1">
      <label class="text-xs text-n-slate-11">
        {{ t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.VARIABLES_LABEL') }}
      </label>
      <div
        v-for="varKey in bodyVariableKeys"
        :key="varKey"
        class="flex items-center gap-2"
      >
        <span class="text-xs font-mono text-n-slate-11 min-w-[3rem]">
          {{ variableLabel(varKey) }}
        </span>
        <input
          type="text"
          class="answer--text-input mb-0 flex-1"
          :placeholder="
            t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.VARIABLE_PLACEHOLDER')
          "
          :value="bodyParams[varKey] || ''"
          @input="updateBodyParam(varKey, $event.target.value)"
        />
      </div>
      <p class="text-xs text-n-slate-10">
        {{ t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.LIQUID_HINT') }}
        <code
          v-for="hint in liquidHints"
          :key="hint"
          class="text-xs px-1 mx-0.5 bg-n-alpha-2 rounded"
        >
          {{ hint }}
        </code>
      </p>
    </div>
    <div v-if="urlButtonComponents.length" class="flex flex-col gap-1">
      <label class="text-xs text-n-slate-11">{{
        t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.BUTTON_URL_PARAMS_LABEL')
      }}</label>
      <div
        v-for="btn in urlButtonComponents"
        :key="btn.originalIndex"
        class="flex items-center gap-2"
      >
        <span class="text-xs font-mono text-n-slate-11 min-w-[6rem] truncate">
          {{ btn.text }}
        </span>
        <input
          type="text"
          class="answer--text-input mb-0 flex-1"
          :placeholder="
            t(
              'AUTOMATION.ACTION.WHATSAPP_TEMPLATE.BUTTON_URL_PARAM_PLACEHOLDER'
            )
          "
          :value="buttonParams[btn.originalIndex]?.parameter || ''"
          @input="updateButtonParam(btn.originalIndex, $event.target.value)"
        />
      </div>
      <p class="text-xs text-n-slate-10">
        {{ t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.LIQUID_HINT') }}
        <code
          v-for="hint in liquidHints"
          :key="hint"
          class="text-xs px-1 mx-0.5 bg-n-alpha-2 rounded"
        >
          {{ hint }}
        </code>
      </p>
    </div>
    <div v-if="inboxId">
      <label class="text-xs text-n-slate-11 mb-1 block">
        {{ t('AUTOMATION.ACTION.FALLBACK_INBOX.LABEL') }}
      </label>
      <select v-model="fallbackInboxId" class="m-0 w-full">
        <option :value="null">
          {{ t('AUTOMATION.ACTION.FALLBACK_INBOX.PLACEHOLDER') }}
        </option>
        <option
          v-for="inbox in fallbackOptions"
          :key="inbox.id"
          :value="inbox.id"
        >
          {{ fallbackOptionLabel(inbox) }}
        </option>
      </select>
      <p class="text-xs text-n-slate-10 mt-1">
        {{ t('AUTOMATION.ACTION.FALLBACK_INBOX.HINT') }}
      </p>
    </div>
  </div>
</template>
